/**
 * shipmentShared.jsx
 *
 * Single source of truth for everything shared between ShipmentsTable
 * and PartialShipmentsTab (and any future shipment view).
 *
 * Exports:
 *  - Constants        : TABLE_HEADERS, ALL_STATUSES, EVENT_OPTIONS, EXCEPTION_OPTIONS,
 *                       statusBadgeClass, DISABLED_STATUSES, DEFAULT_ADVANCED_FILTERS,
 *                       RULE_FIELDS, CONDITION_OPTIONS
 *  - Utils            : makeKey, toBackendStatus, mapDisplayStatus,
 *                       buildFilterNode, countActiveFilters, formatShipmentRow,
 *                       ruleToFilterNode, isRuleComplete, legacyFiltersToRules,
 *                       formatShipmentDisplayText, ShipmentDisplayDetail, PO_SO_DISPLAY_LABELS
 *  - Hook             : useShipmentFilters
 *  - UI Components    : AdvancedFilterToolbar, AdvancedFilterModal,
 *                       ActiveFilterPills
 *
 * Two filter shapes coexist in `advancedFilters` state:
 *  - legacy structured fields (statuses/origin/exceptions/... ) — no view writes
 *    these any more, but saved views persisted before the rule builder still
 *    carry them, so they stay readable (see legacyFiltersToRules)
 *  - `rules[]` + `matchType` — rule-based filtering. `rules` is the root group's
 *    children: each entry is a rule leaf (field + operator + value) or a nested
 *    group with its own `matchType`, so `A and (B or C)` is expressible. Written
 *    by AdvancedFilterModal, which both ShipmentsTable and PartialShipmentsTab
 *    (Drop Trailers) use
 * buildFilterNode/countActiveFilters/pills understand both.
 *
 * Rules saved before Events/Exceptions became individual fields still use the
 * retired `Condition is …` field; normalizeRuleNodes() migrates them on read.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { SlidersHorizontal, X, RotateCcw, Plus, Trash2 } from "lucide-react";
import { FaRegCalendarAlt } from "react-icons/fa";
import { format } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import { setAdvancedFilters as setAdvancedFiltersAction } from "../../../../redux/slices/shipmentSlice";
import { totalChargeFromDTO } from "../../../../utils/chargeUtils";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import {
  ClaimStatusBadge,
} from "../../Claims/claimsShared";

// ─── Constants ────────────────────────────────────────────────────────────────

export const TABLE_HEADERS = [
  "Selection", "Action", "Shipment Number", "Office Code", "Status", "Claims",
  "PO Number", "Sales Order", "Origin", "Destination", "Destination City",
  "Destination State", "Destination Country", "Destination Zip", "Carrier Name",
  "Carrier SCAC Code", "PRO Number", "Equipment Number",
  "Planned Pickup Date", "Planned Delivery Date","Ready for Pickup Date Time", "Actual Pickup Time",
  "Actual Delivery Time", "Pickup Appointment Time", "Invoice Number",
  "Invoice Status", "Pickup Location Reason Name", "Total Charges", "Mode",
  "Created Date", "Updated Date", "Payment Type",
];

export const ALL_STATUSES = [
  "NEW", "ASSIGNED", "UNASSIGNED", "ACCEPTED", "TENDERED",
  "IN TRANSIT", "DELIVERED", "RECALLED", "REJECTED", "CANCELLED",
];

export const EVENT_OPTIONS = [
  "Ready For Pickup",
];

export const EXCEPTION_OPTIONS = [
  "Missing Pickup Appointments",
  "Missing Delivery Appointments",
  "Not Picked Up",
  "Not Delivered",
  "Missing POD",
  "Not Invoiced",
];

export const statusBadgeClass = {
  NEW:              "bg-blue-100    dark:bg-blue-900/40    text-black dark:text-blue-200",
  ASSIGNED:         "bg-amber-100   dark:bg-amber-900/40   text-black dark:text-amber-200",
  UNASSIGNED:       "bg-neutral-200 dark:bg-neutral-700/40 text-black dark:text-gray-200",
  ACCEPTED:         "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  TENDERED:         "bg-indigo-100  dark:bg-indigo-900/40  text-black dark:text-indigo-200",
  "READY FOR PICKUP":"bg-cyan-100   dark:bg-cyan-900/40    text-black dark:text-cyan-200",
  "IN TRANSIT":     "bg-teal-100    dark:bg-teal-900/40    text-black dark:text-teal-200",

  "IN-TRANSIT":     "bg-teal-100    dark:bg-teal-900/40    text-black dark:text-teal-200",
  ACTIVE:           "bg-teal-100    dark:bg-teal-900/40    text-black dark:text-teal-200",
  DELIVERED:        "bg-green-100   dark:bg-green-900/40   text-black dark:text-green-200",
  COMPLETED:        "bg-green-100   dark:bg-green-900/40   text-black dark:text-green-200",
  RECALLED:         "bg-orange-100  dark:bg-orange-900/40  text-black dark:text-orange-200",
  REJECTED:         "bg-rose-100    dark:bg-rose-900/40    text-black dark:text-rose-200",

  INVOICED:         "bg-purple-100  dark:bg-purple-900/40  text-black dark:text-purple-200",
  CANCELLED:        "bg-red-100     dark:bg-red-900/40     text-black dark:text-red-200",
  DEFAULT:          "bg-gray-100    dark:bg-gray-700/40    text-black dark:text-gray-200",
};

/** Kept for the legacy `paymentTypes` filter; not offered as a rule field (see RULE_FIELDS). */
export const PAYMENT_TYPE_OPTIONS = ["PREPAID", "POSTPAY", "COLLECT"];

/** Mirrors the backend MovementType enum / `shipments.movement_type` column. */
export const MODE_OPTIONS = ["FCL", "LCL", "FTL", "LTL", "TL", "AIR", "PARCEL"];


export const DISABLED_STATUSES = new Set(["CANCELLED", "CANCELED"]);

/** LGX-1016: Refresh Rates/Miles are blocked once a shipment is delivered (Completed) and invoiced. */
export const REFRESH_RATES_BLOCKED_MESSAGE =
  "Refreshing rates is blocked for Delivered shipments that are invoiced.";
export const REFRESH_MILES_BLOCKED_MESSAGE =
  "Refreshing miles is blocked for Delivered shipments that are invoiced.";
export const isRefreshBlockedForShipment = (shipmentDetails) =>
  Boolean(shipmentDetails?.isInvoiced) &&
  (shipmentDetails?.status === "Delivered" || shipmentDetails?.status === "Completed");

export const DEFAULT_ADVANCED_FILTERS = {
  statuses: [], origin: "", destination: "", carrierName: "",
  paymentTypes: [], modes: [],
  plannedPickupFrom: "", plannedPickupTo: "",
  plannedDeliveryFrom: "", plannedDeliveryTo: "",
  createdFrom: "", createdTo: "",
  exceptions: [],
  /** Rule-based filters: [{ id, field, op, value }]. */
  rules: [],
  /** How `rules` combine: "AND" (match all) or "OR" (match any). */
  matchType: "AND",
};

/** Group operator for `rules`; anything other than OR behaves as AND. */
export const ruleMatchType = (f) => (f?.matchType === "OR" ? "OR" : "AND");

// ─── Rule-based filtering ─────────────────────────────────────────────────────

/**
 * Pre-canned predicates offered as rule fields under the Events and Exceptions
 * sections of the field picker. Each expands via conditionToFilterNode().
 */
export const CONDITION_OPTIONS = [...EVENT_OPTIONS, ...EXCEPTION_OPTIONS];

/** Rule-field key for a pre-canned Event/Exception predicate. */
export const conditionFieldKey = (condition) => `condition:${condition}`;

/**
 * What each predicate actually matches. Shown in place of a value editor: the
 * field *is* the whole condition, so there is nothing for the user to type.
 */
const CONDITION_DESCRIPTIONS = {
  "Ready For Pickup":              "Ready-for-pickup time is set",
  "Missing Pickup Appointments":   "Accepted, with no pickup appointment",
  "Missing Delivery Appointments": "Accepted, with no delivery appointment",
  "Not Picked Up":                 "Pickup appointment passed, not gated in",
  "Not Delivered":                 "Delivery appointment passed, not completed",
  "Missing POD":                   "No proof of delivery",
  "Not Invoiced":                  "Not yet invoiced",
};

/** `kind` is "Event" or "Exception" — it names both the section and the pill. */
const conditionRuleField = (condition, kind) => ({
  key:         conditionFieldKey(condition),
  label:       condition,
  type:        "condition",
  group:       `${kind}s`,
  kind,
  condition,
  description: CONDITION_DESCRIPTIONS[condition],
  ops:         ["IS"],
});

const TEXT_OPS = ["CONTAINS", "EQ"];

/**
 * Field catalog for the rule builder. Only operators the search endpoint is
 * known to accept are listed (EQ/NEQ/IN/CONTAINS/GTE/LTE).
 *
 *  key          - stable id stored on the rule
 *  type         - drives the value editor: select | multiselect-capable select | text | date | condition
 *  ops          - allowed operators, first one is the default
 *  backendField - field name sent to the API (or backendFields for an OR across several)
 *  group        - optgroup heading in the field picker
 *  mapValue     - optional value transform applied per value before sending
 */
export const RULE_FIELDS = [
  // ── Shipment ────────────────────────────────────────────────────────────
  {
    key: "status", label: "Status", type: "select", group: "Shipment",
    options: ALL_STATUSES, ops: ["EQ", "NEQ", "IN"],
    // Wrapped so the reference resolves lazily (toBackendStatus is declared below).
    backendField: "status", mapValue: (v) => toBackendStatus(v),
  },
  {
    key: "officeCode", label: "Office Code", type: "text", group: "Shipment",
    ops: TEXT_OPS, backendField: "officeCode", placeholder: "e.g. DIAMOND-LAT",
  },
  {
    key: "poNumber", label: "PO Number", type: "text", group: "Shipment",
    ops: TEXT_OPS, backendField: "poNumber", placeholder: "e.g. A1148592",
  },
  {
    key: "salesOrderNumber", label: "Sales Order Number", type: "text", group: "Shipment",
    ops: TEXT_OPS, backendField: "salesOrderNumber", placeholder: "e.g. SP2026051201",
  },
  {
    key: "movementType", label: "Mode", type: "select", group: "Shipment",
    options: MODE_OPTIONS, ops: ["EQ", "NEQ", "IN"],
    backendField: "movementType",
  },
  {
    key: "paymentMethod", label: "Payment Method", type: "select", group: "Shipment",
    options: PAYMENT_TYPE_OPTIONS, ops: ["EQ", "NEQ", "IN"],
    backendField: "paymentMethod",
  },
  /**
   * The `Condition is …` rule these two sections replaced. Hidden from the
   * picker but still resolvable, so views saved before the split keep querying
   * what they say; normalizeRuleNodes() rewrites them on read.
   */
  {
    key: "condition", label: "Condition", type: "condition", group: "Shipment",
    options: CONDITION_OPTIONS, ops: ["IS"], hidden: true,
  },

  // ── Events ──────────────────────────────────────────────────────────────
  ...EVENT_OPTIONS.map((c) => conditionRuleField(c, "Event")),

  // ── Exceptions ──────────────────────────────────────────────────────────
  ...EXCEPTION_OPTIONS.map((c) => conditionRuleField(c, "Exception")),

  // ── Carrier ─────────────────────────────────────────────────────────────
  {
    key: "carrierName", label: "Carrier Name", type: "text", group: "Carrier",
    ops: TEXT_OPS, backendField: "carrierName", placeholder: "e.g. Ascend Freight",
  },
  {
    key: "carrierCode", label: "Carrier SCAC Code", type: "text", group: "Carrier",
    ops: TEXT_OPS, backendField: "carrierCode", placeholder: "e.g. ASCD",
  },

  // ── Route ───────────────────────────────────────────────────────────────
  {
    key: "location", label: "Location (Origin or Destination)", type: "text", group: "Route",
    ops: TEXT_OPS, backendFields: ["origin", "destination"],
    placeholder: "e.g. Costco",
  },
  {
    key: "origin", label: "Origin", type: "text", group: "Route",
    ops: TEXT_OPS, backendField: "origin",
    placeholder: "e.g. North Valley Logistics Park",
  },
  {
    key: "destination", label: "Destination", type: "text", group: "Route",
    ops: TEXT_OPS, backendField: "destination",
    placeholder: "e.g. Central Regional Warehouse",
  },
  {
    key: "pickupLocationReasonName", label: "Pickup Location Reason Name",
    type: "text", group: "Route",
    ops: TEXT_OPS, backendField: "pickupLocationReasonName",
    placeholder: "e.g. Drop Trailer",
  },

  // ── Origin address (pickup stop) ────────────────────────────────────────
  {
    key: "originCity", label: "Origin City", type: "text", group: "Origin address",
    ops: TEXT_OPS, backendField: "originCity", placeholder: "e.g. Sunrise",
  },
  {
    key: "originState", label: "Origin State", type: "text", group: "Origin address",
    ops: TEXT_OPS, backendField: "originState", placeholder: "e.g. FL",
  },
  {
    key: "originZipCode", label: "Origin Zip", type: "text", group: "Origin address",
    ops: TEXT_OPS, backendField: "originZipCode", placeholder: "e.g. 33351",
  },
  {
    key: "originCountry", label: "Origin Country", type: "text", group: "Origin address",
    ops: TEXT_OPS, backendField: "originCountry", placeholder: "e.g. US",
  },

  // ── Destination address (delivery stop) ─────────────────────────────────
  {
    key: "destinationCity", label: "Destination City", type: "text", group: "Destination address",
    ops: TEXT_OPS, backendField: "destinationCity", placeholder: "e.g. Twin Falls",
  },
  {
    key: "destinationState", label: "Destination State", type: "text", group: "Destination address",
    ops: TEXT_OPS, backendField: "destinationState", placeholder: "e.g. ID",
  },
  {
    key: "destinationZipCode", label: "Destination Zip", type: "text", group: "Destination address",
    ops: TEXT_OPS, backendField: "destinationZipCode", placeholder: "e.g. 83301",
  },
  {
    key: "destinationCountry", label: "Destination Country", type: "text", group: "Destination address",
    ops: TEXT_OPS, backendField: "destinationCountry", placeholder: "e.g. US",
  },

  // ── Dates ───────────────────────────────────────────────────────────────
  {
    key: "pickupDate", label: "Planned Pickup Date", type: "date", group: "Dates",
    ops: ["GTE", "LTE"], backendField: "pickupDate",
  },
  {
    key: "deliveryDate", label: "Planned Delivery Date", type: "date", group: "Dates",
    ops: ["GTE", "LTE"], backendField: "deliveryDate",
  },
  {
    key: "createdDate", label: "Created Date", type: "date", group: "Dates",
    ops: ["GTE", "LTE"], backendField: "createdDate",
  },
];

/** Field keys grouped for the picker, preserving RULE_FIELDS order. */
export const RULE_FIELD_GROUPS = RULE_FIELDS.reduce((groups, field) => {
  if (field.hidden) return groups;
  const name = field.group || "Other";
  const existing = groups.find((g) => g.name === name);
  if (existing) existing.fields.push(field);
  else groups.push({ name, fields: [field] });
  return groups;
}, []);

const OPERATOR_LABELS = {
  EQ:       "equals",
  NEQ:      "does not equal",
  IN:       "is any of",
  CONTAINS: "contains",
  GTE:      "is on or after",
  LTE:      "is on or before",
  IS:       "is",
};

export const ruleFieldByKey = (key) => RULE_FIELDS.find((f) => f.key === key);

export const operatorLabel = (op) => OPERATOR_LABELS[op] ?? op;

/** Values are stored as an array for IN, a plain string otherwise. */
export const ruleValueIsMulti = (rule) => rule?.op === "IN";

export const makeRuleId = () =>
  `rule-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const makeRule = (fieldKey = "status", overrides = {}) => {
  const field = ruleFieldByKey(fieldKey) ?? RULE_FIELDS[0];
  const op = overrides.op ?? field.ops[0];
  return {
    id: makeRuleId(),
    field: field.key,
    op,
    // An Events/Exceptions field is the whole predicate, so it carries its own value.
    value: field.condition ?? (op === "IN" ? [] : ""),
    ...overrides,
  };
};

/** A rule only reaches the API once it has a field, a valid operator and a value. */
export const isRuleComplete = (rule) => {
  const field = ruleFieldByKey(rule?.field);
  if (!field || !field.ops.includes(rule.op)) return false;
  if (Array.isArray(rule.value)) return rule.value.length > 0;
  return String(rule.value ?? "").trim().length > 0;
};

export const describeRule = (rule) => {
  const field = ruleFieldByKey(rule?.field);
  if (!field) return "";
  const value = Array.isArray(rule.value) ? rule.value.join(", ") : rule.value;
  if (field.type === "condition")
    return `${field.kind ?? "Condition"}: ${field.condition ?? value}`;
  return `${field.label} ${operatorLabel(rule.op)} ${value}`;
};

// ─── Nested rule groups ───────────────────────────────────────────────────────
//
// `rules` is the root group's child list. A child is either a rule leaf
// (`{ id, field, op, value }`) or a nested group (`{ id, matchType, children }`),
// which lets the tree express `A and (B or C)`. State written before groups
// existed is a list of leaves, so it loads unchanged.

/** Root counts as depth 1, so this allows two levels of nesting below it. */
export const MAX_RULE_DEPTH = 3;

export const isRuleGroup = (node) => Array.isArray(node?.children);

export const makeRuleGroup = (matchType = "OR", children = []) => ({
  id: `group-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
  matchType: matchType === "OR" ? "OR" : "AND",
  children,
});

/** A group earns its place once at least one descendant leaf is complete. */
export const isNodeComplete = (node) =>
  isRuleGroup(node)
    ? node.children.some(isNodeComplete)
    : isRuleComplete(node);

/** Drops incomplete leaves and any group left empty by that pruning. */
export const pruneRuleNodes = (nodes = []) =>
  nodes.reduce((kept, node) => {
    if (!isRuleGroup(node)) {
      if (isRuleComplete(node)) kept.push(node);
      return kept;
    }
    const children = pruneRuleNodes(node.children);
    if (children.length) kept.push({ ...node, children });
    return kept;
  }, []);

/**
 * Rewrites rules saved before Events/Exceptions became their own fields, so a
 * `Condition is Missing POD` leaf becomes the `Missing POD` field. Without this
 * an old saved view would open on a field the picker no longer offers.
 */
export const normalizeRuleNodes = (nodes = []) =>
  nodes.map((node) => {
    if (isRuleGroup(node)) return { ...node, children: normalizeRuleNodes(node.children) };
    if (node?.field !== "condition") return node;
    const condition = Array.isArray(node.value) ? node.value[0] : node.value;
    return CONDITION_OPTIONS.includes(condition)
      ? { ...node, field: conditionFieldKey(condition), op: "IS", value: condition }
      : node;
  });

/** Every rule leaf in the tree, at any depth. */
export const flattenRuleLeaves = (nodes = []) =>
  nodes.flatMap((node) => (isRuleGroup(node) ? flattenRuleLeaves(node.children) : [node]));

export const countRuleNodes = (nodes = []) => flattenRuleLeaves(nodes).length;

/** True when any group in the tree joins its children with OR. */
export const treeHasOrGroup = (nodes = []) =>
  nodes.some(
    (node) =>
      isRuleGroup(node) &&
      (ruleMatchType(node) === "OR" || treeHasOrGroup(node.children))
  );

/** How a row joins the row above it: its own `join`, else the group's operator (older saves). */
export const nodeJoin = (node, groupMatch) =>
  node?.join === "OR" || node?.join === "AND" ? node.join : groupMatch;

/** Splits siblings into AND-runs; each "or" starts a new run, so `A or B and C` is `A or (B and C)`. */
const splitIntoAndRuns = (entries, groupMatch) =>
  entries.reduce((runs, entry, i) => {
    if (i === 0 || nodeJoin(entry.node, groupMatch) === "OR") runs.push([entry]);
    else runs[runs.length - 1].push(entry);
    return runs;
  }, []);

/** Reads a subtree back as text, e.g. `Status equals NEW or (A and B)`. */
export const describeNode = (node) => {
  if (!isRuleGroup(node)) return describeRule(node);
  const entries = node.children.filter(isNodeComplete).map((child) => ({ node: child, text: describeNode(child) }));
  const runs = splitIntoAndRuns(entries, ruleMatchType(node)).map((run) => {
    const text = run.map((e) => e.text).join(" and ");
    return run.length > 1 && entries.length > run.length ? `(${text})` : text;
  });
  const inner = runs.join(" or ");
  return entries.length > 1 ? `(${inner})` : inner;
};

// Immutable tree edits, all keyed by node id.

export const updateRuleNode = (nodes = [], id, updater) =>
  nodes.map((node) => {
    if (node.id === id) return updater(node);
    if (isRuleGroup(node))
      return { ...node, children: updateRuleNode(node.children, id, updater) };
    return node;
  });

export const removeRuleNode = (nodes = [], id) =>
  nodes
    .filter((node) => node.id !== id)
    .map((node) =>
      isRuleGroup(node) ? { ...node, children: removeRuleNode(node.children, id) } : node
    );

/** Appends `child` to the group with `groupId`; a null id targets the root list. */
export const appendRuleNode = (nodes = [], groupId, child) => {
  if (!groupId) return [...nodes, child];
  return updateRuleNode(nodes, groupId, (group) =>
    isRuleGroup(group) ? { ...group, children: [...group.children, child] } : group
  );

};

/** Default status quick-filter (FilterButton) when no saved view is active. */
export const DEFAULT_QUICK_FILTER = "New";

export const quickFilterMatchesView = (saved, current) =>
  saved === undefined || (saved ?? null) === (current ?? null);

export const advancedFiltersMatchView = (saved, current) => {
  if (saved === undefined) return true;
  const a = saved ?? DEFAULT_ADVANCED_FILTERS;
  const b = current ?? DEFAULT_ADVANCED_FILTERS;
  return JSON.stringify(a) === JSON.stringify(b);
};

// ─── Utils ────────────────────────────────────────────────────────────────────

export const makeKey = (shipmentNumber, officeCode) =>
  `${shipmentNumber}::${officeCode}`;

/** Replace commas with semicolons for multi-value PO / sales order display. */
export const formatShipmentDisplayText = (value) => {
  if (value == null || value === "") return value;
  if (typeof value === "string") {
    return value.includes(",") ? value.replace(/,/g, ";") : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (React.isValidElement(value)) return value;
  const text = String(value);
  return text.includes(",") ? text.replace(/,/g, ";") : value;
};

/** Labels for detail/header fields that should use comma → semicolon formatting. */
export const PO_SO_DISPLAY_LABELS = new Set([
  "PO Number",
  "PO Numbers",
  "Sales Order",
  "SO Number",
  "Sales Order Number",
  "Sales Order Numbers",
]);

const poSoCell = (value) => {
  if (value == null || value === "") return "-";
  return formatShipmentDisplayText(value);
};

/** Read-only detail field — formats PO / sales order values only. */
export const ShipmentDisplayDetail = ({
  value,
  label,
  isEditable = false,
  isReadOnly = false,
  ...props
}) => {
  const shouldFormat = label && PO_SO_DISPLAY_LABELS.has(label);
  const displayValue =
    isEditable && !isReadOnly
      ? value
      : shouldFormat
        ? formatShipmentDisplayText(value)
        : value;

  return (
    <DisplayDetail
      {...props}
      label={label}
      isEditable={isEditable}
      isReadOnly={isReadOnly}
      value={displayValue}
    />
  );
};

/** Formats sticky-header PO / SO fields for read-only shipment detail view. */
export const formatShipmentInfoDataForDisplay = (infoData = {}) =>
  Object.fromEntries(
    Object.entries(infoData).map(([key, field]) => {
      if (!field || typeof field !== "object") return [key, field];
      if (PO_SO_DISPLAY_LABELS.has(key) && typeof field.value === "string") {
        return [key, { ...field, value: formatShipmentDisplayText(field.value) }];
      }
      return [key, field];
    })
  );

// Backend status matching is case-insensitive, so this is just a case transform now —
// the raw persisted value already is the display word ("In Transit", "Delivered", ...).
export const toBackendStatus = (s) => (s ? s.toUpperCase() : s);

/**
 * The quick-filter status still worth sending, given the advanced filters.
 *
 * Only filters that state a status themselves take it over — that is the same test
 * `buildFilterNode` applies before adding a status node, and the same one the
 * filter modal applies when it clears the quick filter on apply. Standing any
 * active filter down instead left a tab holding a status it never sent: a tab
 * saved as "Delivered + ACME" queried every ACME shipment, in every status.
 */
export const resolveQuickStatusFilter = (quickFilter, advancedFilters) =>
  filtersOverrideStatus(advancedFilters ?? DEFAULT_ADVANCED_FILTERS)
    ? null
    : (quickFilter || null);

export const mapDisplayStatus = (status) => (status ? status.toUpperCase() : "-");

// 30-minute grace window used by exception filters
const graceTimestamp = () =>
  new Date(Date.now() - 30 * 60 * 1000).toISOString().replace("Z", "");

/**
 * Expands a pre-canned Event/Exception name into a backend FilterNode.
 * Shared by the legacy `exceptions` array and rule-based `Condition is …` rules.
 */
export const conditionToFilterNode = (condition) => {
  const g = graceTimestamp();
  switch (condition) {
    case "Missing Pickup Appointments":
      return { operator: "AND", filters: [
        { field: "pickupAppointmentStartDate", op: "IS_NULL" },
        { field: "status", op: "EQ", value: "Accepted" },
      ]};
    case "Missing Delivery Appointments":
      return { operator: "AND", filters: [
        { field: "deliveryAppointmentStartDate", op: "IS_NULL" },
        { field: "status", op: "EQ", value: "Accepted" },
      ]};
    case "Ready For Pickup":
      // UI: Events section. Backend: exception predicate — expands IS_NOT_NULL
      // into RFP-set + PICKUP gate in/out empty.
      return { field: "readyForPickupDateTime", op: "IS_NOT_NULL" };
    case "Not Picked Up":
      return { operator: "AND", filters: [
        { field: "actualGateIn",               op: "IS_NULL" },
        { field: "pickupAppointmentStartDate", op: "IS_NOT_NULL" },
        { field: "pickupDate",                 op: "LTE", value: g },
      ]};
    case "Not Delivered":
      return { operator: "AND", filters: [
        { field: "status",                      op: "NEQ",      value: "Completed" },
        { field: "actualGateOut",               op: "IS_NULL" },
        { field: "deliveryAppointmentStartDate",op: "IS_NOT_NULL" },
        { field: "deliveryDate",                op: "LTE", value: g },
      ]};
    case "Missing POD":
      return { field: "missingPod", op: "IS_TRUE" };
    case "Not Invoiced":
      return { field: "isInvoiced", op: "IS_FALSE" };
    default:
      return null;
  }
};

/** Turns a single rule-builder rule into a backend FilterNode (null if incomplete). */
export const ruleToFilterNode = (rule) => {
  const field = ruleFieldByKey(rule?.field);
  if (!field || !isRuleComplete(rule)) return null;

  if (field.type === "condition")
    return conditionToFilterNode(field.condition ?? rule.value);

  const values = (Array.isArray(rule.value) ? rule.value : [rule.value])
    .map((v) => String(v).trim())
    .filter(Boolean)
    .map((v) => (field.mapValue ? field.mapValue(v) : v));
  if (!values.length) return null;

  const value = rule.op === "IN" ? values.join(",") : values[0];

  // Multi-target fields (e.g. Location) match if ANY of their backend fields match.
  if (field.backendFields)
    return {
      operator: "OR",
      filters: field.backendFields.map((f) => ({ field: f, op: rule.op, value })),
    };

  return { field: field.backendField, op: rule.op, value };
};

/**
 * Turns a rule node (leaf or group) into a backend FilterNode.
 * Groups with a single surviving child collapse to that child, so nesting never
 * adds pointless wrapper levels to the payload.
 */
export const nodeToFilterNode = (node) => {
  if (!isRuleGroup(node)) return ruleToFilterNode(node);
  return combineSiblings(node.children, ruleMatchType(node));
};

/**
 * Joins sibling nodes using each row's own and/or. AND binds tighter than OR, so the
 * result is an OR of AND-runs; single-item runs and single-run results collapse.
 */
export const combineSiblings = (nodes, groupMatch) => {
  const entries = nodes
    .map((node) => ({ node, filter: nodeToFilterNode(node) }))
    .filter((e) => e.filter);
  if (!entries.length) return null;
  const runs = splitIntoAndRuns(entries, groupMatch).map((run) =>
    run.length === 1 ? run[0].filter : { operator: "AND", filters: run.map((e) => e.filter) }
  );
  return runs.length === 1 ? runs[0] : { operator: "OR", filters: runs };
};

/** Top-level complete nodes — one pill each. */
export const activeRuleNodes = (f) => pruneRuleNodes(normalizeRuleNodes(f?.rules || []));

/** Every complete leaf in the tree — used for counting and status checks. */
export const activeRuleLeaves = (f) => flattenRuleLeaves(activeRuleNodes(f));

// Cancelled-shipment visibility is enforced server-side, and differs between the two
// ways of asking:
//  - filtering (a FilterNode, no `query`): cancelled rows stay hidden unless explicitly
//    asked for — a `status` param of "cancelled", or a status leaf that can match
//    CANCELLED. See ShipmentDynamicSpecification#requestsCancelled.
//  - searching (a `query` string): status no longer narrows anything, so cancelled
//    matches come back too. Typing a shipment's own number is an explicit request for
//    that one shipment, and hiding it returned zero results for a third of the grid.
//    See ShipmentSearchJpql#STATUS_FILTER_SEARCH.
// Deliberately not duplicated here, so the policy has one home.

/**
 * True when the advanced filters already constrain status, so the quick-filter
 * status dropdown must not be sent alongside them.
 *
 * In "match any" (OR) mode ANY rule overrides it: AND-ing a tab status onto an
 * OR group would silently drop the rows the other branches are meant to let in
 * (e.g. `Status = NEW OR Location = Costco` must still return Costco rows that
 * aren't NEW).
 */
export const filtersOverrideStatus = (f) => {
  if (f?.statuses?.length) return true;
  const nodes = activeRuleNodes(f);
  if (!nodes.length) return false;
  const rootMatch = ruleMatchType(f);
  if (nodes.some((node, i) => i > 0 && nodeJoin(node, rootMatch) === "OR")) return true;
  // A status rule anywhere in the tree — including inside a nested group — means
  // the user is expressing status intent themselves.
  return flattenRuleLeaves(nodes).some((r) => r.field === "status");
};

/**
 * Builds a backend FilterNode tree from advanced filter state.
 *
 * @param {object}  adv             - advanced filter values
 * @param {string}  topLevelStatus  - status from the active tab (e.g. "Active")
 */
export const buildFilterNode = (adv, topLevelStatus) => {
  const nodes = [];

  if (topLevelStatus && !filtersOverrideStatus(adv))
    nodes.push({ field: "status", op: "EQ", value: toBackendStatus(topLevelStatus) });

  if (adv.statuses.length === 1)
    nodes.push({ field: "status", op: "EQ", value: toBackendStatus(adv.statuses[0]) });
  else if (adv.statuses.length > 1)
    nodes.push({ field: "status", op: "IN", value: adv.statuses.map(toBackendStatus).join(",") });

  if (adv.origin?.trim())
    nodes.push({ field: "origin",       op: "CONTAINS", value: adv.origin.trim() });
  if (adv.destination?.trim())
    nodes.push({ field: "destination",  op: "CONTAINS", value: adv.destination.trim() });
  if (adv.carrierName?.trim())
    nodes.push({ field: "carrierName",  op: "CONTAINS", value: adv.carrierName.trim() });

  if (adv.paymentTypes.length === 1)
    nodes.push({ field: "paymentMethod", op: "EQ", value: adv.paymentTypes[0] });
  else if (adv.paymentTypes.length > 1)
    nodes.push({ field: "paymentMethod", op: "IN", value: adv.paymentTypes.join(",") });

  if (adv.modes.length === 1)
    nodes.push({ field: "movementType", op: "EQ", value: adv.modes[0] });
  else if (adv.modes.length > 1)
    nodes.push({ field: "movementType", op: "IN", value: adv.modes.join(",") });

  if (adv.plannedPickupFrom)   nodes.push({ field: "pickupDate",   op: "GTE", value: adv.plannedPickupFrom });
  if (adv.plannedPickupTo)     nodes.push({ field: "pickupDate",   op: "LTE", value: adv.plannedPickupTo });
  if (adv.plannedDeliveryFrom) nodes.push({ field: "deliveryDate", op: "GTE", value: adv.plannedDeliveryFrom });
  if (adv.plannedDeliveryTo)   nodes.push({ field: "deliveryDate", op: "LTE", value: adv.plannedDeliveryTo });
  if (adv.createdFrom)         nodes.push({ field: "createdDate",  op: "GTE", value: adv.createdFrom });
  if (adv.createdTo)           nodes.push({ field: "createdDate",  op: "LTE", value: adv.createdTo });

  // Legacy `exceptions` multi-select: any-of (OR) semantics.
  const exceptionNodes = (adv.exceptions || [])
    .map(conditionToFilterNode)
    .filter(Boolean);

  if (exceptionNodes.length === 1) nodes.push(exceptionNodes[0]);
  else if (exceptionNodes.length > 1) nodes.push({ operator: "OR", filters: exceptionNodes });

  // Rule tree: each row carries its own and/or to the row above (AND binds
  // tighter), with nested groups combining their own children the same way.
  // A pure-AND result is pushed flat so the payload stays identical to a
  // single-level AND; anything with an OR is one group AND-ed with the legacy
  // nodes above it.
  const ruleTree = combineSiblings(adv.rules || [], ruleMatchType(adv));
  if (ruleTree?.operator === "AND") nodes.push(...ruleTree.filters);
  else if (ruleTree) nodes.push(ruleTree);


  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0];
  return { operator: "AND", filters: nodes };
};

/**
 * Builds the POST body for the shipment search endpoints.
 *
 * Lives here because ShipmentsTable and PartialShipmentsTab (Drop Trailers) each used to
 * assemble it inline, and they drifted: the shipments grid learned to drop the quick-filter
 * status while searching, Drop Trailers did not, so searching there only ever looked inside
 * the selected tab status (which defaults to "New") and found nothing the rest of the time.
 *
 * The rule, in one place: a typed query narrows within whatever quick status / advanced
 * filters are already active, the same as it would with an empty query — a search isn't a
 * separate, unscoped mode. With no filter active, that scope is simply everything, so the
 * search is effectively global, matching what Drop Trailers already relied on.
 *
 * @param {string}  searchQuery           - raw search box text ("" when not searching)
 * @param {string}  filter                - quick-filter status from the tab
 * @param {object}  advancedFilters       - advanced filter state
 * @param {string}  sortBy                - backend sort key, or null
 * @param {boolean} ascending             - sort direction
 * @param {string}  filterType            - ALL | DROP_TRAILER | NOT_INVOICED
 * @param {boolean} includeAdvancedFilters - false for views with no filter UI
 */
export const buildShipmentSearchPayload = ({
  searchQuery,
  filter,
  advancedFilters,
  sortBy = null,
  ascending = false,
  filterType,
  includeAdvancedFilters = true,
}) => {
  const query = (searchQuery || "").trim() || null;

  const quickStatus = resolveQuickStatusFilter(filter, advancedFilters);

  const filterNode = includeAdvancedFilters
    ? buildFilterNode(advancedFilters, quickStatus)
    : null;

  return {
    query,
    sortBy,
    filterType,
    ascending,
    status: quickStatus ? toBackendStatus(quickStatus) : null,
    ...(filterNode ? { filter: filterNode } : {}),
  };
};

export const countActiveFilters = (f) => {
  const selectedEvents = (f.exceptions || []).filter((ex) => EVENT_OPTIONS.includes(ex));
  const selectedExceptions = (f.exceptions || []).filter((ex) => !EVENT_OPTIONS.includes(ex));
  let n = 0;
  if (f.statuses.length)                          n++;
  if (selectedEvents.length)                      n++;
  if (selectedExceptions.length)                  n++;
  if (f.origin.trim())                            n++;
  if (f.destination.trim())                       n++;
  if (f.carrierName.trim())                       n++;
  if (f.paymentTypes.length)                      n++;
  if (f.modes.length)                             n++;
  if (f.plannedPickupFrom  || f.plannedPickupTo)  n++;
  if (f.plannedDeliveryFrom|| f.plannedDeliveryTo)n++;
  if (f.createdFrom        || f.createdTo)        n++;
  n += activeRuleLeaves(f).length;

  return n;
};

/**
 * Converts legacy structured filter state into equivalent rules, so a view saved
 * with the old modal (or the active quick-filter status) opens pre-populated in
 * the rule builder.
 */
export const legacyFiltersToRules = (f = {}, quickFilterStatus = null) => {
  const rules = [];
  const push = (fieldKey, op, value) => rules.push(makeRule(fieldKey, { op, value }));

  if (f.statuses?.length === 1)      push("status", "EQ", f.statuses[0]);
  else if (f.statuses?.length > 1)   push("status", "IN", [...f.statuses]);
  else {
    const display = quickFilterStatus ? mapDisplayStatus(quickFilterStatus) : null;
    if (display && ALL_STATUSES.includes(display)) push("status", "EQ", display);
  }

  if (f.origin?.trim())      push("origin",      "CONTAINS", f.origin.trim());
  if (f.destination?.trim()) push("destination", "CONTAINS", f.destination.trim());
  if (f.carrierName?.trim()) push("carrierName", "CONTAINS", f.carrierName.trim());

  if (f.paymentTypes?.length === 1)    push("paymentMethod", "EQ", f.paymentTypes[0]);
  else if (f.paymentTypes?.length > 1) push("paymentMethod", "IN", [...f.paymentTypes]);

  if (f.modes?.length === 1)    push("movementType", "EQ", f.modes[0]);
  else if (f.modes?.length > 1) push("movementType", "IN", [...f.modes]);

  if (f.plannedPickupFrom)   push("pickupDate",   "GTE", f.plannedPickupFrom);
  if (f.plannedPickupTo)     push("pickupDate",   "LTE", f.plannedPickupTo);
  if (f.plannedDeliveryFrom) push("deliveryDate", "GTE", f.plannedDeliveryFrom);
  if (f.plannedDeliveryTo)   push("deliveryDate", "LTE", f.plannedDeliveryTo);
  if (f.createdFrom)         push("createdDate",  "GTE", f.createdFrom);
  if (f.createdTo)           push("createdDate",  "LTE", f.createdTo);

  // Legacy exceptions were OR'd; as rules they become AND'd conditions. Carry
  // them over anyway — the user can drop the ones they don't want.
  for (const condition of f.exceptions || [])
    push(conditionFieldKey(condition), "IS", condition);

  return rules;
};

/**

 * Formats a raw API shipment object into a display-ready table row map.
 *
 * Handles two data shapes transparently:
 *  - ShipmentsTable shape : { totalCharge, appointmentTime, paymentMethod, ... }
 *  - PartialShipmentsTab  : { charges[], pickupAppointmentStartDate, freightPaymentTermCode, ... }
 */
export const formatShipmentRow = (shipment, { isCarrier = false, role = "" } = {}) => {
  // All roles display the REVENUE sum. DB totalCharge (mixed) is never surfaced.
  const _t = totalChargeFromDTO(
    shipment?.revenueTotalCharge,
    shipment?.costTotalCharge,
    isCarrier ? "carrier" : role
  );
  const totalCharges = _t.revenue ?? _t.total;

  // Support both field name conventions for appointment time
  const apptRaw = shipment?.pickupAppointmentStartDate ?? shipment?.appointmentTime;

  return {
    __hasChildShipments:         shipment?.__hasChildShipments,
    __isChildShipment:           shipment?.__isChildShipment,
    __parentShipmentNumber:      shipment?.__parentShipmentNumber,
    "Shipment Number":           shipment?.shipmentNumber ?? "-",
    "Office Code":               shipment?.officeCode ?? "-",
    "Status": (
      <span className={`text-black text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium ${
        statusBadgeClass[(shipment?.status || "").toUpperCase()] ?? statusBadgeClass.DEFAULT
      }`}>
        {mapDisplayStatus(shipment?.status)}
      </span>
    ),
    "Claims": (() => {
      if (!shipment?.hasClaims && !shipment?.primaryClaimStatusLabel) {
        return <span className="text-[#c3c6d6] font-semibold">—</span>;
      }
      return <ClaimStatusBadge status={shipment.primaryClaimStatusLabel} />;
    })(),
    "PO Number":                 poSoCell(shipment?.poNumber),
    "Sales Order":               poSoCell(shipment?.salesOrderNumber),
    // Port first, stop name as the fallback. A walk-up check-in has no port - nothing at a gate
    // knows one - but it always has the origin/destination the guard typed, which is what these
    // columns are read as meaning. Ports keep precedence so nothing changes for freight that
    // actually has one.
    "Origin":                    shipment?.portOfOrigin || shipment?.originName || "-",
    "Destination":               shipment?.portOfDestination || shipment?.destinationName || "-",
    "Destination City":          shipment?.destinationCity ?? "-",
    "Destination State":         shipment?.destinationState ?? "-",
    "Destination Country":       shipment?.destinationCountry ?? "-",
    "Destination Zip":           shipment?.destinationZipCode ?? "-",
    "Carrier Name":              shipment?.carrierName ?? "-",
    "Carrier SCAC Code":         shipment?.carrierCode ?? "-",
    "PRO Number":                shipment?.proNumber ?? "-",
    "Equipment Number":          shipment?.equipmentNumber ?? "-",
    "Planned Pickup Date":       shipment?.pickupDate
      ? format(new Date(shipment.pickupDate),   "yyyy-MM-dd")        : "-",
    "Planned Delivery Date":     shipment?.deliveryDate
      ? format(new Date(shipment.deliveryDate), "yyyy-MM-dd")        : "-",
    "Ready for Pickup Date Time": shipment?.readyForPickupDateTime
      ? format(new Date(shipment.readyForPickupDateTime), "yyyy-MM-dd HH:mm") : "-",
    "Actual Pickup Time":        shipment?.actualGateIn
      ? format(new Date(shipment.actualGateIn), "yyyy-MM-dd HH:mm") : "-",
    "Actual Delivery Time":      shipment?.actualGateOut
      ? format(new Date(shipment.actualGateOut),"yyyy-MM-dd HH:mm") : "-",
    "Pickup Appointment Time":   apptRaw
      ? format(new Date(apptRaw),               "yyyy-MM-dd HH:mm") : "-",
    "Invoice Number":            shipment?.invoiceNumber ?? "-",
    "Invoice Status":            shipment?.invoiceNumber && shipment.invoiceNumber !== "-"
      ? (shipment?.invoiceStatus ?? "-")
      : "Not Invoiced",
    "Pickup Location Reason Name": shipment?.pickupLocationReasonName ?? "-",
    "Total Charges":             totalCharges ? `$${totalCharges.toFixed(2)}` : "-",
    "Mode":                      shipment?.movementType ?? "-",
    "Created Date":              shipment?.createdDate
      ? format(new Date(shipment.createdDate),  "yyyy-MM-dd HH:mm") : "-",
    "Updated Date":              shipment?.updatedDate
      ? format(new Date(shipment.updatedDate),  "yyyy-MM-dd HH:mm") : "-",
    // Support both field name conventions for payment type
    "Payment Type":              shipment?.freightPaymentTermCode ?? shipment?.paymentMethod ?? "-",
  };
};

// ─── Hook: useShipmentFilters ─────────────────────────────────────────────────

/**
 * Manages advanced filter state for a shipment view.
 *
 * @param {string}  stateKey  - unique key for this view's slice of state
 * @param {boolean} persist   - true  → Redux (survives tab switches, page refreshes)
 *                              false → component-local useState
 *
 * Both variants expose the same API so consumers are agnostic to the backing store.
 */
export const useShipmentFilters = ({ stateKey, persist = false }) => {
  const dispatch = useDispatch();

  // Always call useSelector — hooks cannot be called conditionally.
  // The value is only consumed when persist=true.
  const reduxFilters = useSelector(
    (s) => s.shipments.advancedFilters?.[stateKey] ?? DEFAULT_ADVANCED_FILTERS
  );
  const [localFilters, setLocalFilters] = useState(DEFAULT_ADVANCED_FILTERS);

  const filters = persist ? reduxFilters : localFilters;

  const updateFilters = useCallback(
    (newFilters) => {
      if (persist) {
        dispatch(setAdvancedFiltersAction({ stateKey, filters: newFilters }));
      } else {
        setLocalFilters(newFilters);
      }
    },
    [persist, stateKey, dispatch]
  );

  const clearFilters = useCallback(
    () => updateFilters(DEFAULT_ADVANCED_FILTERS),
    [updateFilters]
  );

  /** Removes a single filter pill by its key (`rule:<id>` for rule-based pills). */
  const removePill = useCallback(
    (key) => {
      if (typeof key === "string" && key.startsWith("rule:")) {
        // Works for leaves and whole groups — ids are unique across the tree.
        updateFilters({
          ...filters,
          rules: removeRuleNode(filters.rules || [], key.slice("rule:".length)),
        });
        return;
      }
      const resets = {
        statuses:     { statuses: [] },
        events:       { exceptions: filters.exceptions.filter((ex) => !EVENT_OPTIONS.includes(ex)) },
        exceptions:   { exceptions: filters.exceptions.filter((ex) => EVENT_OPTIONS.includes(ex)) },
        origin:       { origin: "" },
        destination:  { destination: "" },
        carrierName:  { carrierName: "" },
        paymentTypes: { paymentTypes: [] },
        modes:        { modes: [] },
        pickup:       { plannedPickupFrom: "", plannedPickupTo: "" },
        delivery:     { plannedDeliveryFrom: "", plannedDeliveryTo: "" },
        created:      { createdFrom: "", createdTo: "" },
      };
      updateFilters({ ...filters, ...(resets[key] ?? {}) });
    },
    [filters, updateFilters]
  );

  return {
    filters,
    updateFilters,
    clearFilters,
    removePill,
    activeCount: countActiveFilters(filters),
  };
};

// ─── Date picker control ──────────────────────────────────────────────────────

/** Date input used by the rule builder's `date` fields. */
const DatePickerButton = ({ value, onChange, min }) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const formatted = value
    ? (([y, m, d]) => `${m} / ${d} / ${y}`)(value.split("-"))
    : "";

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className="relative flex items-center w-full px-3 py-1.5 text-sm rounded-lg border
        overflow-hidden border-gray-300 dark:border-acs-dark-border bg-white
        dark:bg-acs-dark-surface focus-within:ring-2 focus-within:ring-blue-500
        focus-within:border-blue-500 transition-colors cursor-pointer"
    >
      <span
        className={`flex-1 ${formatted ? "text-gray-900 dark:text-acs-dark-text" : "text-gray-400 dark:placeholder-gray-500"}
          ${focused ? "invisible" : ""}`}
      >
        {formatted || "mm / dd / yyyy"}
      </span>

      <FaRegCalendarAlt
        size={14}
        onClick={(e) => {
          e.stopPropagation(); // prevent wrapper's focus() from racing with showPicker()
          inputRef.current?.showPicker();
        }}
        className={`text-custom-bg-blue dark:text-acs-accent flex-shrink-0 cursor-pointer ${focused ? "invisible" : ""}`}
      />

      <input
        ref={inputRef}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`absolute left-0 top-0 h-full pl-3 bg-transparent outline-none
          text-gray-900 dark:text-acs-dark-text
          ${focused
            ? "opacity-100 w-[calc(100%+40px)] pointer-events-auto"
            : "opacity-0 w-full pointer-events-none"
          }`}
      />
    </div>
  );
};


export const DateRangeRow = ({ label, fromValue, toValue, onFromChange, onToChange }) => (
  <div className="grid grid-cols-[120px_1fr_1fr] items-center gap-3">
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
    <DatePickerButton value={fromValue} onChange={onFromChange} />
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-400 flex-shrink-0">to</span>
      <div className="flex-1">
        <DatePickerButton value={toValue} onChange={onToChange} min={fromValue || undefined} />
      </div>
    </div>
  </div>
);


// ─── Active filter pills ──────────────────────────────────────────────────────

export const ActiveFilterPills = ({ filters, onRemove }) => {
  const selectedEvents = (filters.exceptions || []).filter((ex) => EVENT_OPTIONS.includes(ex));
  const selectedExceptions = (filters.exceptions || []).filter((ex) => !EVENT_OPTIONS.includes(ex));
  const rootMatch = ruleMatchType(filters);
  const pills = [
    filters.statuses.length     && { key: "statuses",    label: `Status: ${filters.statuses.join(", ")}` },
    selectedEvents.length       && { key: "events",      label: `Events: ${selectedEvents.join(", ")}` },
    selectedExceptions.length   && { key: "exceptions",  label: `Exceptions: ${selectedExceptions.join(", ")}` },
    filters.origin.trim()       && { key: "origin",      label: `Origin: ${filters.origin}` },
    filters.destination.trim()  && { key: "destination", label: `Destination: ${filters.destination}` },
    filters.carrierName.trim()  && { key: "carrierName", label: `Carrier: ${filters.carrierName}` },
    filters.paymentTypes.length && { key: "paymentTypes",label: `Payment: ${filters.paymentTypes.join(", ")}` },
    filters.modes.length        && { key: "modes",       label: `Mode: ${filters.modes.join(", ")}` },
    (filters.plannedPickupFrom || filters.plannedPickupTo) && {
      key: "pickup",
      label: `Pickup: ${filters.plannedPickupFrom || "..."} → ${filters.plannedPickupTo || "..."}`,
    },
    (filters.plannedDeliveryFrom || filters.plannedDeliveryTo) && {
      key: "delivery",
      label: `Delivery: ${filters.plannedDeliveryFrom || "..."} → ${filters.plannedDeliveryTo || "..."}`,
    },
    (filters.createdFrom || filters.createdTo) && {
      key: "created",
      label: `Created: ${filters.createdFrom || "..."} → ${filters.createdTo || "..."}`,
    },
    // One pill per top-level node — a nested group shows as `(A or B)` and is
    // removed as a unit. Join words keep "match any" from reading as "match all".
    ...activeRuleNodes(filters).flatMap((node, i) => [
      ...(i > 0
        ? [{ key: `join:${node.id}`, label: nodeJoin(node, rootMatch) === "OR" ? "or" : "and", joiner: true }]
        : []),
      { key: `rule:${node.id}`, label: describeNode(node) },
    ]),

  ].filter(Boolean);

  if (!pills.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2">
      {pills.map((p) =>
        p.joiner ? (
          <span
            key={p.key}
            className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500"
          >
            {p.label}
          </span>
        ) : (
          <span
            key={p.key}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
          >
            {p.label}
            <button
              onClick={() => onRemove(p.key)}
              className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
            >
              <X size={11} />
            </button>
          </span>
        )
      )}
    </div>
  );
};

// ─── Advanced Filter Modal ────────────────────────────────────────────────────

// Shared look for every control in a rule row; padding is applied per control
// type below so a <select> can reserve room for its native arrow.
const RULE_CONTROL_BASE =
  "w-full text-sm rounded-lg border border-gray-300 dark:border-acs-dark-border " +
  "bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";

/** Text inputs: symmetric padding, no arrow to clear. */
const RULE_CONTROL_CLASS = `${RULE_CONTROL_BASE} px-2.5 py-1.5`;

/**
 * Selects need extra right padding or long options (e.g. "Location (Origin or
 * Destination)") run underneath the native dropdown arrow; `truncate` then
 * ellipsises anything still too long instead of hard-clipping it.
 */
const RULE_SELECT_CLASS = `${RULE_CONTROL_BASE} pl-2.5 pr-8 py-1.5 truncate`;

/** The compact and/or selector — same idea, tighter arrow clearance. */
const RULE_CONJUNCTION_CLASS =
  `${RULE_CONTROL_BASE} pl-2 pr-6 py-1.5 text-[11px] font-semibold uppercase tracking-wide`;

/** Keeps the stored value shape consistent with the operator (array for IN). */
const coerceValueForOp = (value, nextOp) => {
  if (nextOp === "IN") {
    if (Array.isArray(value)) return value;
    const single = String(value ?? "").trim();
    return single ? [single] : [];
  }
  if (Array.isArray(value)) return value[0] ?? "";
  return value ?? "";
};

const RuleValueEditor = ({ rule, field, onChange, statusOptions }) => {
  // LGX-1164: a carrier login only gets to pick from its own visible statuses —
  // other fields, and callers that don't pass an override, keep the full list.
  const options =
    field.key === "status" && statusOptions?.length ? statusOptions : field.options;

  // Events/Exceptions are whole predicates picked by name in the field column,
  // so this cell explains the match rather than asking for a value.
  if (field.type === "condition")
    return (
      <p className="px-2.5 py-1.5 text-xs text-gray-500 dark:text-gray-400">
        {field.description ?? (Array.isArray(rule.value) ? rule.value[0] : rule.value)}
      </p>
    );

  if (field.type === "date")
    return <DatePickerButton value={rule.value || ""} onChange={onChange} />;

  if (field.type === "select") {
    if (rule.op === "IN") {
      const selected = Array.isArray(rule.value) ? rule.value : [];
      return (
        <div className="flex flex-wrap gap-1.5 py-0.5">
          {options.map((o) => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() =>
                  onChange(on ? selected.filter((v) => v !== o) : [...selected, o])
                }
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all ${
                  on
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-transparent border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-blue-400 hover:text-blue-600"
                }`}
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <select
        value={Array.isArray(rule.value) ? rule.value[0] ?? "" : rule.value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={RULE_SELECT_CLASS}
      >
        <option value="">Select a value…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={Array.isArray(rule.value) ? rule.value.join(", ") : rule.value || ""}
      placeholder={field.placeholder || "Enter a value"}
      onChange={(e) => onChange(e.target.value)}
      className={`${RULE_CONTROL_CLASS} placeholder-gray-400 dark:placeholder-gray-500`}
    />
  );
};

const AddNodeButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-dashed border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
  >
    <Plus size={13} /> {children}
  </button>
);

/** One condition: field + operator + value. */
const RuleLeafRow = ({ rule, actions, statusOptions }) => {
  const field = ruleFieldByKey(rule.field) ?? RULE_FIELDS[0];
  const incomplete = !isRuleComplete(rule);

  return (
    <div
      className={`grid grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,1.25fr)] items-start gap-2 rounded-lg border px-2 py-2 ${
        incomplete
          ? "border-acs-secondary-purple/60 dark:border-acs-secondary-purple/50 bg-custom-bg-light-purple/50 dark:bg-acs-secondary-purple/10"
          : "border-gray-200 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface"
      }`}
    >
      <select
        value={rule.field}
        onChange={(e) => actions.changeField(rule, e.target.value)}
        title={field.label}
        className={RULE_SELECT_CLASS}
      >
        {RULE_FIELD_GROUPS.map((group) => (
          <optgroup key={group.name} label={group.name}>
            {group.fields.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      <select
        value={rule.op}
        onChange={(e) => actions.changeOp(rule, e.target.value)}
        disabled={field.ops.length === 1}
        className={`${RULE_SELECT_CLASS} ${
          field.ops.length === 1 ? "opacity-60 cursor-not-allowed" : ""
        }`}
      >
        {field.ops.map((op) => (
          <option key={op} value={op}>{operatorLabel(op)}</option>
        ))}
      </select>

      <RuleValueEditor
        rule={rule}
        field={field}
        onChange={(v) => actions.changeValue(rule.id, v)}
        statusOptions={statusOptions}
      />
    </div>
  );
};

/**
 * A nested group: its own and/or plus its own children, rendered by the same
 * list component one level down. Mutually recursive with RuleNodeList — both are
 * module-level consts, so the references resolve at render time.
 */
const RuleGroupBox = ({ group, depth, actions, statusOptions }) => {
  const groupMatch = ruleMatchType(group);

  return (
    <div className="rounded-lg border border-gray-300 dark:border-acs-dark-border bg-gray-50 dark:bg-white/[0.03] px-2 py-2">
      <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Group
      </p>

      {group.children.length === 0 ? (
        <p className="px-1 pb-1 text-xs text-gray-400 dark:text-gray-500">
          Empty group — add a condition or it will be dropped.
        </p>
      ) : (
        <RuleNodeList
          nodes={group.children}
          matchType={groupMatch}
          groupId={group.id}
          depth={depth + 1}
          actions={actions}
          statusOptions={statusOptions}
        />
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        <AddNodeButton onClick={() => actions.addRule(group.id)}>Add condition</AddNodeButton>
        {depth + 1 < MAX_RULE_DEPTH && (
          <AddNodeButton onClick={() => actions.addGroup(group.id, groupMatch)}>
            Add group
          </AddNodeButton>
        )}
      </div>
    </div>
  );
};

/**
 * Renders one group's children. Every row after the first has its own and/or selector
 * that joins it to the row above; `matchType` is only the default for rows that have
 * never been set (older saves). Nested groups combine their own children the same way.
 */
const RuleNodeList = ({ nodes, matchType, groupId, depth, actions, statusOptions }) => (
  <div className="space-y-2">
    {nodes.map((node, idx) => {
      const isGroup = isRuleGroup(node);

      return (
        <div key={node.id} className="flex items-start gap-2">
          <div className="w-[74px] flex-shrink-0">
            {idx >= 1 ? (
              <select
                value={nodeJoin(node, matchType)}
                onChange={(e) => actions.setJoin(node.id, e.target.value)}
                aria-label="Join this condition to the one above with and / or"
                className={RULE_CONJUNCTION_CLASS}
              >
                <option value="AND">and</option>
                <option value="OR">or</option>
              </select>
            ) : (
              <span className="block pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Where
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {isGroup ? (
              <RuleGroupBox group={node} depth={depth} actions={actions} statusOptions={statusOptions} />
            ) : (
              <RuleLeafRow rule={node} actions={actions} statusOptions={statusOptions} />
            )}
          </div>

          <button
            type="button"
            onClick={() => actions.remove(node.id)}
            title={isGroup ? "Remove group" : "Remove condition"}
            className="mt-1.5 flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      );
    })}
  </div>
);

/**
 * Rule-based advanced filtering.
 *
 * Each row is one condition (field + operator + value). The and/or selector on a
 * group's second row sets how that group joins:
 *   AND — `Status equals NEW` and `Location contains Costco` (both must hold)
 *   OR  — `Status equals NEW` or  `Location contains Costco` (either may hold)
 * "Add group" nests a sub-group with its own operator, so mixed logic such as
 * `Status = NEW and (Location = Costco or Carrier = Ascend)` is expressible;
 * one operator per group means precedence is always explicit. The former
 * Events/Exceptions checkboxes are their own fields, under the field picker's
 * Events and Exceptions sections.
 *
 * Applies as `{ ...DEFAULT_ADVANCED_FILTERS, matchType, rules }` — legacy
 * structured fields are migrated into rules on open, so nothing is filtered twice.
 */
export const AdvancedFilterModal = ({ filters, quickFilterStatus, onApply, onClose, statusOptions }) => {
  const [rules, setRules] = useState(() =>
    filters?.rules?.length
      ? normalizeRuleNodes(filters.rules).map((r) => ({ ...r, id: r.id ?? makeRuleId() }))
      : legacyFiltersToRules(filters, quickFilterStatus)
  );
  const [matchType, setMatchType] = useState(() => ruleMatchType(filters));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const actions = {
    changeField: (rule, nextFieldKey) => {
      const nextField = ruleFieldByKey(nextFieldKey);
      if (!nextField) return;
      const nextOp = nextField.ops.includes(rule.op) ? rule.op : nextField.ops[0];
      setRules((prev) =>
        updateRuleNode(prev, rule.id, (r) => ({
          ...r,
          field: nextFieldKey,
          op: nextOp,
          // Events/Exceptions carry their own value; every other field starts empty.
          value: nextField.condition ?? coerceValueForOp("", nextOp),
        }))
      );
    },
    changeOp: (rule, nextOp) =>
      setRules((prev) =>
        updateRuleNode(prev, rule.id, (r) => ({
          ...r,
          op: nextOp,
          value: coerceValueForOp(r.value, nextOp),
        }))
      ),
    changeValue: (id, value) =>
      setRules((prev) => updateRuleNode(prev, id, (r) => ({ ...r, value }))),
    remove: (id) => setRules((prev) => removeRuleNode(prev, id)),
    addRule: (groupId) =>
      setRules((prev) =>
        appendRuleNode(
          prev,
          groupId,
          makeRule(!groupId && prev.length === 0 ? "status" : "location")
        )
      ),
    // A new group defaults to the opposite operator of its parent — that is the
    // case nesting exists for (an "any of" alternative inside a "match all").
    addGroup: (groupId, parentMatchType) =>
      setRules((prev) =>
        appendRuleNode(
          prev,
          groupId,
          makeRuleGroup(parentMatchType === "AND" ? "OR" : "AND", [
            makeRule("location"),
            makeRule("carrierName"),
          ])
        )
      ),
    setJoin: (id, join) =>
      setRules((prev) => updateRuleNode(prev, id, (n) => ({ ...n, join }))),
  };

  const prunedRules = pruneRuleNodes(rules);
  const activeCount  = countRuleNodes(prunedRules);
  const hasIncomplete = countRuleNodes(rules) > activeCount;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">
              Advanced Filters
            </h2>
            {activeCount > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-600 text-white">
                {activeCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Choose <span className="font-semibold">and</span> or{" "}
            <span className="font-semibold">or</span> between each condition.
            <span className="ml-1 text-gray-400 dark:text-gray-500">
              <span className="font-semibold">and</span> is applied before{" "}
              <span className="font-semibold">or</span>; use{" "}
              <span className="font-semibold">Add group</span> for explicit brackets.
            </span>
          </p>

          {rules.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-acs-dark-border px-4 py-6 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No conditions yet — every shipment is shown.
              </p>
            </div>
          ) : (
            <RuleNodeList
              nodes={rules}
              matchType={matchType}
              groupId={null}
              depth={1}
              actions={actions}
              statusOptions={statusOptions}
            />
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <AddNodeButton onClick={() => actions.addRule(null)}>Add condition</AddNodeButton>
            <AddNodeButton onClick={() => actions.addGroup(null, matchType)}>
              Add group
            </AddNodeButton>
          </div>

          {hasIncomplete && (
            <p className="mt-3 text-xs text-custom-bg-blue dark:text-acs-secondary-purple">
              Conditions without a value are ignored, as are empty groups.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-acs-dark-border flex-shrink-0 bg-gray-50 dark:bg-acs-dark-surface">
          <button
            onClick={() => { setRules([]); setMatchType("AND"); }}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            <RotateCcw size={14} /> Reset all
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply({ ...DEFAULT_ADVANCED_FILTERS, matchType, rules: prunedRules });
                onClose();
              }}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
            >
              Apply Filters
              {activeCount > 0 && (
                <span className="ml-1.5 bg-white/25 text-white text-xs rounded-full px-1.5 py-0.5">
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Composite toolbar (button + pills) ──────────────────────────────────────

/**
 * Drop-in "Advanced Filters" toolbar. Renders the toggle button, optional
 * "Clear all" link, and the active filter pills row.
 *
 * Usage:
 *   const { filters, activeCount, updateFilters, clearFilters, removePill }
 *     = useShipmentFilters({ stateKey, persist });
 *
 *   <AdvancedFilterToolbar
 *     filters={filters}
 *     activeCount={activeCount}
 *     onOpenModal={() => setShowModal(true)}
 *     onClearAll={clearFilters}
 *     onRemovePill={removePill}
 *   />
 */
export const AdvancedFilterToolbar = ({
  filters,
  activeCount,
  onOpenModal,
  onClearAll,
  onRemovePill,
  trailing = null,
}) => (
  <>
    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0">
      <div className="flex items-center gap-2">
      <button
        onClick={onOpenModal}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150 ${
          activeCount > 0
            ? "bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
            : "bg-white dark:bg-acs-dark-surface border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
        }`}
      >
        <SlidersHorizontal size={15} />
        Advanced Filters
        {activeCount > 0 && (
          <span className="ml-0.5 bg-white/25 text-white text-xs font-semibold rounded-full px-1.5 py-0.5 leading-none">
            {activeCount}
          </span>
        )}
      </button>

      {activeCount > 0 && (
        <button
          onClick={onClearAll}
          className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
        >
          <RotateCcw size={12} /> Clear all
        </button>
      )}
      </div>
      {trailing}
    </div>

    {activeCount > 0 && (
      <div className="pt-2">
        <ActiveFilterPills filters={filters} onRemove={onRemovePill} />
      </div>
    )}
  </>
);